/**
 * Issue tracker — shared types + enums.
 * Spec: docs/categorized/features/ISSUE-TRACKER-PRD.md (§4 data model).
 *
 * Firestore layout:
 *   issues/{key}                     IssueDoc        (key = 'ISS-012', also the doc id)
 *   issues/{key}/comments/{id}       IssueCommentDoc
 *   config/issueBoard                IssueBoardDoc   (columns == statuses)
 *   config/issueCounter              IssueCounterDoc ({ next })
 * Storage: issues/{key}/{attachmentId}-{safeName}
 *
 * 2026-09-09: access to the tracker is the standard module pattern (module key
 * 'issue-tracker', tier-3 feature 'issues') — the bespoke config/issueTracker
 * access flag and its IssueTrackerConfigDoc/canUseIssueTracker helper are gone.
 *
 * Timestamps are typed loosely (`unknown`) because the frontend uses the compat SDK's
 * `firebase.firestore.Timestamp` and scripts use firebase-admin's — the shape is the same.
 */

export const ISSUE_TYPES = ['bug', 'feature', 'task'] as const;
export type IssueType = (typeof ISSUE_TYPES)[number];

export const ISSUE_PRIORITIES = ['low', 'medium', 'high', 'critical'] as const;
export type IssuePriority = (typeof ISSUE_PRIORITIES)[number];

export const ISSUE_RELEASED_TO = ['none', 'develop', 'staging', 'prod'] as const;
export type IssueReleasedTo = (typeof ISSUE_RELEASED_TO)[number];

export const ISSUE_ENVIRONMENTS = ['develop', 'staging', 'prod', 'unknown'] as const;
export type IssueEnvironment = (typeof ISSUE_ENVIRONMENTS)[number];

/** Doc id prefix + zero-padding width ('ISS-012'). Past 999 the key grows to 4 digits; sort by `number`. */
export const ISSUE_KEY_PREFIX = 'ISS-';
export const ISSUE_KEY_PAD = 3;

export const ISSUE_DEFAULT_COLUMNS: readonly IssueBoardColumn[] = Object.freeze([
    { id: 'open', name: 'Open', order: 0 },
    { id: 'in-progress', name: 'In Progress', order: 1 },
    { id: 'review', name: 'Review', order: 2 },
    { id: 'closed', name: 'Closed', order: 3, isDone: true },
]);

export interface IssuePerson {
    uid: string | null;
    name: string;
    email?: string | null;
}

export interface IssueAttachment {
    id: string;
    name: string;
    /** Storage path: issues/{key}/{id}-{safeName} */
    path: string;
    url: string;
    type: string;
    size: number;
    uploadedBy: string;
    uploadedByName: string;
    uploadedAt: unknown;
}

export interface IssueDoc {
    key: string;
    number: number;
    title: string;
    description: string;
    /** "Which screen were you on?" — free text; pre-filled with the route the reporter came from. */
    screen: string;
    type: IssueType;
    priority: IssuePriority;
    /** A column id from config/issueBoard. */
    status: string;
    releasedTo: IssueReleasedTo;
    environment: IssueEnvironment;
    orgId: string | null;
    orgName: string | null;
    reporter: IssuePerson;
    developer: IssuePerson | null;
    stepsToReproduce: string;
    expected: string;
    actual: string;
    build: { hash: string; env: string } | null;
    client: { userAgent: string; viewport: string; platform: 'web' | 'android' | 'ios' } | null;
    labels: string[];
    dueDate: unknown | null;
    resolution: string;
    attachments: IssueAttachment[];
    /** Manual position inside its column; default -createdAt millis (newest on top). */
    order: number;
    commentCount: number;
    attachmentCount: number;
    lastActivityAt: unknown;
    closedAt: unknown | null;
    reopenCount: number;
    /** v2 archive (soft delete): absent/false = live. Archived issues leave list/board/stats
     *  by default and can be restored; only an archived issue may be hard-deleted (admin). */
    archived?: boolean;
    archivedAt?: unknown | null;
    archivedBy?: IssuePerson | null;
    createdAt: unknown;
    createdBy: string;
    updatedAt: unknown;
    updatedBy: string;
    /** 'YYYY-MM-DD' */
    dateReported: string;
    legacy?: { sheetRow: number; screenshotNote?: string };
}

export type IssueCommentKind = 'comment' | 'system';

export interface IssueFieldChange {
    field: string;
    from: string | null;
    to: string | null;
}

export interface IssueCommentDoc {
    kind: IssueCommentKind;
    author: IssuePerson;
    body: string;
    attachments: IssueAttachment[];
    change?: IssueFieldChange;
    /** v2 @mentions: the people named in `body` as `@Display Name`; drives the highlight and
     *  the `mentioned_in_document` notification. Absent on every pre-v2 comment. */
    mentions?: IssuePerson[];
    createdAt: unknown;
    editedAt?: unknown | null;
}

export interface IssueBoardColumn {
    id: string;
    name: string;
    order: number;
    color?: string;
    wipLimit?: number | null;
    /** Moving into this column stamps closedAt; moving out increments reopenCount. */
    isDone?: boolean;
}

/**
 * Colours assignable per type / priority / release (Paul, 2026-09-09): hex strings keyed by the
 * value. Stored on the board doc so every surface that already streams the board gets them
 * without another listener; a missing key falls back to ISSUE_DEFAULT_COLORS.
 */
export interface IssueColorConfig {
    type: Partial<Record<IssueType, string>>;
    priority: Partial<Record<IssuePriority, string>>;
    releasedTo: Partial<Record<IssueReleasedTo, string>>;
}

export const ISSUE_DEFAULT_COLORS: Readonly<IssueColorConfig> = Object.freeze({
    type: { bug: '#fde0d0', feature: '#fff3cd', task: '#e0ecf7' },
    priority: { low: '#ececec', medium: '#fff3cd', high: '#fde0d0', critical: '#fddede' },
    releasedTo: { none: '#f4f0ec', develop: '#e0ecf7', staging: '#e3f2e5', prod: '#ede0f7' },
});

export interface IssueBoardDoc {
    version: number;
    columns: IssueBoardColumn[];
    colors?: IssueColorConfig;
    updatedAt: unknown;
    updatedBy: string;
}

export interface IssueCounterDoc {
    next: number;
}

/** 'ISS-012' for 12; 'ISS-1000' for 1000. */
export function issueKeyFor(n: number): string {
    return `${ISSUE_KEY_PREFIX}${String(n).padStart(ISSUE_KEY_PAD, '0')}`;
}
